import { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { PayrollRunFiltersOptionsDocument } from 'generated/resources/graphql'

import { countryName } from 'modules/core/lib/countryList'

type FilterOption = { value: string; label: string }
type FilterOptions = FilterOption[]

type CyclesListFilterOptions = {
  countryFilterOptions: FilterOptions
  currencyFilterOptions: FilterOptions
  loading: boolean
}

/**
 * Provides filter options for cycles list based on payroll run filters.
 *
 * This hook fetches the required data using a query and processes it to generate
 * unique country and currency filter options. The results include country and currency
 * options, as well as a loading state to track the query's progress.
 *
 * @return {CyclesListFilterOptions} An object containing the following properties:
 * - `countryFilterOptions`: An array of filter options for countries, where each option
 *   is represented by an object with `value` and `label` properties.
 * - `currencyFilterOptions`: An array of filter options for currencies, where each
 *   option is represented by an object with `value` and `label` properties.
 * - `loading`: A boolean indicating whether the query is still in progress.
 */
function useCyclesListFilterOptions(): CyclesListFilterOptions {
  // It is an indirection to compute the filters from the query of the company's legal entities because this criterion
  // could change in the future, and this logic would need to be refactored.
  // A more accurate approach would be to calculate the countries and currencies used by going through the list of
  // payroll runs, but this has the disadvantage that you have to go through the entire list of payroll runs every time
  // they come from the server.
  // We have decided on the first approach because we can accept this indirect approach for now until a more powerful
  // way of creating payroll runs is defined.
  const { data: payrollRunFiltersOptions, loading } = useQuery(PayrollRunFiltersOptionsDocument)

  const { countryFilterOptions, currencyFilterOptions } = useMemo(() => {
    if (!payrollRunFiltersOptions?.companies.legalEntitiesConnection.nodes.length) {
      return { countryFilterOptions: [], currencyFilterOptions: [] }
    }

    const uniqueCountryOptions = new Set()
    const countryOptions: FilterOptions = []
    const uniqueCurrencyOptions = new Set()
    const currencyOptions: FilterOptions = []

    payrollRunFiltersOptions.companies.legalEntitiesConnection.nodes.forEach((le) => {
      if (!uniqueCountryOptions.has(le.country)) {
        uniqueCountryOptions.add(le.country)
        countryOptions.push({
          value: le.country,
          label: countryName(le.country),
        })
      }

      if (!uniqueCurrencyOptions.has(le.currency)) {
        uniqueCurrencyOptions.add(le.currency)
        currencyOptions.push({
          value: le.currency,
          label: le.currency,
        })
      }
    })

    return {
      countryFilterOptions: countryOptions,
      currencyFilterOptions: currencyOptions,
    }
  }, [payrollRunFiltersOptions])

  return {
    countryFilterOptions,
    currencyFilterOptions,
    loading,
  }
}

export { useCyclesListFilterOptions }
export type { CyclesListFilterOptions }
